import { useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";

/** "Paste an email" fallback: raw source (with headers) or the plain sender/subject/body fields. */
export default function PasteEmailDialog({ open, onOpenChange, onImported }) {
  const [mode, setMode] = useState("raw");
  const [raw, setRaw] = useState("");
  const [f, setF] = useState({ from_email: "", from_name: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = mode === "raw" ? { raw } : { ...f, raw: "" };
      const { data } = await API.post("/inbox/paste", payload);
      toast.success(data.status === "new" ? "Email imported" : data.status === "added" ? `Threaded onto ${data.job_request_ref}` : data.status === "converted" ? `Created ${data.job_request_ref}` : "Imported (auto-ignored as bulk / auto-reply)");
      setRaw(""); setF({ from_email: "", from_name: "", subject: "", body: "" });
      onImported?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="paste-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Paste an email</DialogTitle>
          <DialogDescription>Import an email by hand — handy before the mailbox is connected, or for something forwarded to you.</DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="h-8">
            <TabsTrigger value="raw" className="text-xs" data-testid="paste-mode-raw">Full source (with headers)</TabsTrigger>
            <TabsTrigger value="fields" className="text-xs" data-testid="paste-mode-fields">Sender, subject & body</TabsTrigger>
          </TabsList>
          <TabsContent value="raw" className="mt-3">
            <Textarea value={raw} onChange={e => setRaw(e.target.value)} rows={12} className="font-mono text-xs" placeholder={"From: Sarah Jones <sarah@client.co.uk>\nTo: maintenance@yourcompany.co.uk\nSubject: Leak under the sink\nDate: Mon, 1 Jun 2026 09:12:00 +0100\n\nHi team, there is a leak…"} data-testid="paste-raw-textarea" />
            <p className="text-[11px] text-muted-foreground mt-1">In most mail apps: open the message → "Show original" / "View source" → copy everything.</p>
          </TabsContent>
          <TabsContent value="fields" className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Sender email</Label><Input value={f.from_email} onChange={e => setF(p => ({ ...p, from_email: e.target.value }))} placeholder="sarah@client.co.uk" className="mt-1" data-testid="paste-from-email" /></div>
              <div><Label className="text-xs">Sender name</Label><Input value={f.from_name} onChange={e => setF(p => ({ ...p, from_name: e.target.value }))} placeholder="Sarah Jones" className="mt-1" data-testid="paste-from-name" /></div>
            </div>
            <div><Label className="text-xs">Subject</Label><Input value={f.subject} onChange={e => setF(p => ({ ...p, subject: e.target.value }))} className="mt-1" data-testid="paste-subject" /></div>
            <div><Label className="text-xs">Body</Label><Textarea value={f.body} onChange={e => setF(p => ({ ...p, body: e.target.value }))} rows={8} className="mt-1 text-xs" data-testid="paste-body" /></div>
          </TabsContent>
        </Tabs>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (mode === "raw" ? !raw.trim() : !f.from_email.trim())} data-testid="paste-submit">{busy ? "Importing…" : "Import email"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
